import { Container, Graphics } from 'pixi.js';

export const WORLD_W = 8000;
export const WORLD_H = 8000;

const ISLAND_COUNT = 60;
const MIN_SEP      = 280; // minimum center-to-center distance

export interface Island {
  cx: number;
  cy: number;
  radius: number; // bounding radius (used for collision)
  points: { x: number; y: number }[];
}

// Deterministic seeded RNG (mulberry32)
function makeRng(seed: number): () => number {
  let s = seed;
  return function () {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// djb2 string hash — used to seed per-island RNGs from a server-issued id so
// every client derives the exact same shape without shipping the polygon
// over the wire.
function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return h >>> 0;
}

/**
 * A player-built island. Only `id`, `cx`, `cy` come from the server — the
 * shape is regenerated deterministically from `id` so it matches on every
 * client (same trick as the static islands, just seeded per-island instead
 * of from one shared generation pass).
 */
export function makeBuiltIsland(id: string, cx: number, cy: number): Island {
  const rng   = makeRng(hashString(id));
  const baseR = 90;
  const points = generateIslandPoints(cx, cy, baseR, rng);
  return { cx, cy, radius: baseR * 1.3, points };
}

/** Deterministic tree-placement RNG for a built island, seeded independently
 *  of its shape RNG so tree layout isn't just a mirror of the vertex noise. */
export function treeRngForBuiltIsland(id: string): () => number {
  return makeRng(hashString(id + ':trees'));
}

function generateIslandPoints(
  cx: number,
  cy: number,
  baseR: number,
  rng: () => number,
): { x: number; y: number }[] {
  const N      = 10 + Math.floor(rng() * 4); // 10-13 vertices
  const phase1 = rng() * Math.PI * 2;
  const phase2 = rng() * Math.PI * 2;
  const amp1   = 0.25 + rng() * 0.20;
  const amp2   = 0.08 + rng() * 0.10;
  const points: { x: number; y: number }[] = [];

  for (let i = 0; i < N; i++) {
    const angle = (i / N) * Math.PI * 2;
    const noise =
      1
      + amp1 * Math.sin(2 * angle + phase1)
      + amp2 * Math.sin(5 * angle + phase2);
    const r = baseR * noise;
    points.push({ x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r });
  }
  return points;
}

export function generateIslands(): Island[] {
  const rng     = makeRng(0xdeadbeef);
  const islands: Island[] = [];

  while (islands.length < ISLAND_COUNT) {
    const cx = rng() * WORLD_W;
    const cy = rng() * WORLD_H;

    let tooClose = false;
    for (const other of islands) {
      const dx = cx - other.cx;
      const dy = cy - other.cy;
      if (Math.sqrt(dx * dx + dy * dy) < MIN_SEP) { tooClose = true; break; }
    }
    if (tooClose) continue;

    const baseR  = 80 + rng() * 140; // 80-220 unit radius
    const points = generateIslandPoints(cx, cy, baseR, rng);
    islands.push({ cx, cy, radius: baseR * 1.3, points });
  }

  return islands;
}

function drawIslandShape(
  g: Graphics,
  points: { x: number; y: number }[],
  ox: number,
  oy: number,
): void {
  g.moveTo(points[0].x + ox, points[0].y + oy);
  for (let i = 1; i < points.length; i++) {
    g.lineTo(points[i].x + ox, points[i].y + oy);
  }
  g.closePath();
}

export function buildWorldContainer(islands: Island[]): Container {
  const worldContainer = new Container();

  // Debris and floating objects scattered across the water
  const debrisRng = makeRng(0xf00dcafe);
  const treeRng = makeRng(0xbeef1234);
  const debris    = new Graphics();
  const DEBRIS_COUNT = 1800;

  for (let i = 0; i < DEBRIS_COUNT; i++) {
    const x    = debrisRng() * WORLD_W;
    const y    = debrisRng() * WORLD_H;
    const kind = debrisRng();

    // Skip debris that would land on an island
    let onIsland = false;
    for (const isl of islands) {
      const dx = x - isl.cx, dy = y - isl.cy;
      if (dx * dx + dy * dy < isl.radius * isl.radius) { onIsland = true; break; }
    }
    if (onIsland) continue;

    if (kind < 0.40) {
      // Foam streak — short white dashes
      const angle = debrisRng() * Math.PI;
      const len   = 6 + debrisRng() * 14;
      debris.moveTo(x, y);
      debris.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len);
      debris.stroke({ color: 0xd0eef8, alpha: 0.30 + debrisRng() * 0.25, width: 1 + debrisRng() });

    } else if (kind < 0.65) {
      // Seaweed patch — small dark green blob
      const r = 3 + debrisRng() * 5;
      debris.ellipse(x, y, r, r * (0.5 + debrisRng() * 0.7));
      debris.fill({ color: 0x2a6640, alpha: 0.35 + debrisRng() * 0.25 });

    } else if (kind < 0.82) {
      // Floating log / plank — thin brown rectangle
      const angle = debrisRng() * Math.PI;
      const len   = 10 + debrisRng() * 20;
      const w     = 2 + debrisRng() * 2;
      const cx2   = x + Math.cos(angle) * len / 2;
      const cy2   = y + Math.sin(angle) * len / 2;
      debris.moveTo(cx2 - Math.cos(angle) * len / 2 - Math.sin(angle) * w,
                    cy2 - Math.sin(angle) * len / 2 + Math.cos(angle) * w);
      debris.lineTo(cx2 + Math.cos(angle) * len / 2 - Math.sin(angle) * w,
                    cy2 + Math.sin(angle) * len / 2 + Math.cos(angle) * w);
      debris.lineTo(cx2 + Math.cos(angle) * len / 2 + Math.sin(angle) * w,
                    cy2 + Math.sin(angle) * len / 2 - Math.cos(angle) * w);
      debris.lineTo(cx2 - Math.cos(angle) * len / 2 + Math.sin(angle) * w,
                    cy2 - Math.sin(angle) * len / 2 - Math.cos(angle) * w);
      debris.closePath();
      debris.fill({ color: 0x7a5228, alpha: 0.55 + debrisRng() * 0.2 });

    } else {
      // Small rock / buoy dot
      const r = 2 + debrisRng() * 3;
      debris.circle(x, y, r);
      debris.fill({ color: debrisRng() < 0.5 ? 0x8a7a6a : 0xcc3333, alpha: 0.55 + debrisRng() * 0.3 });
    }
  }

  worldContainer.addChild(debris);

  for (const island of islands) {
    const g = new Graphics();
    drawSingleIsland(g, island, treeRng);
    worldContainer.addChild(g);
  }

  return worldContainer;
}

// ---- Flora: trees and bushes scattered across each island ----
type PlantKind = 'oak' | 'pine' | 'palm' | 'birch' | 'willow' | 'baobab' | 'berry-bush' | 'flower-bush' | 'fern-bush';
const PLANT_KINDS: PlantKind[] = ['oak', 'pine', 'palm', 'birch', 'willow', 'baobab', 'berry-bush', 'flower-bush', 'fern-bush'];

/** One plant, at (x, y), roughly `size` units tall. `rng` may be consumed
 *  further for per-plant variation — every client runs the same code against
 *  the same seeded stream, so this stays deterministic regardless of how
 *  many extra calls any one branch makes. */
function drawPlant(g: Graphics, x: number, y: number, size: number, kind: PlantKind, rng: () => number): void {
  switch (kind) {
    case 'oak': {
      g.ellipse(x + 2, y + size * 0.6, size * 0.7, size * 0.3);
      g.fill({ color: 0x000000, alpha: 0.15 });
      g.moveTo(x, y);
      g.lineTo(x, y + size * 0.8);
      g.stroke({ color: 0x5a3a1a, width: 3, cap: 'round' });
      g.circle(x, y, size);
      g.fill({ color: 0x2a6e30 });
      g.circle(x, y, size * 0.75);
      g.fill({ color: 0x347a3a });
      break;
    }
    case 'pine': {
      g.ellipse(x + 2, y + size * 0.7, size * 0.6, size * 0.25);
      g.fill({ color: 0x000000, alpha: 0.15 });
      g.moveTo(x, y + size * 0.3);
      g.lineTo(x, y + size * 0.9);
      g.stroke({ color: 0x4a3018, width: 2.5, cap: 'round' });
      // Stacked triangle tiers, narrowing toward the top
      for (let tier = 0; tier < 3; tier++) {
        const tw = size * (0.9 - tier * 0.25);
        const ty0 = y - size * 0.5 + tier * size * 0.4;
        g.moveTo(x, ty0 - size * 0.35);
        g.lineTo(x + tw * 0.5, ty0 + size * 0.2);
        g.lineTo(x - tw * 0.5, ty0 + size * 0.2);
        g.closePath();
        g.fill({ color: tier === 1 ? 0x1e5a28 : 0x256030 });
      }
      break;
    }
    case 'palm': {
      g.ellipse(x + 2, y + size * 0.7, size * 0.7, size * 0.28);
      g.fill({ color: 0x000000, alpha: 0.15 });
      // Curved trunk
      const lean = (rng() - 0.5) * size * 0.4;
      g.moveTo(x, y + size * 0.9);
      g.quadraticCurveTo(x + lean * 0.6, y + size * 0.3, x + lean, y - size * 0.3);
      g.stroke({ color: 0x8a6a3a, width: 2.5 });
      // Fronds radiating from the top
      const top = { x: x + lean, y: y - size * 0.3 };
      for (let i = 0; i < 5; i++) {
        const a = (i / 4) * Math.PI - Math.PI / 2;
        g.moveTo(top.x, top.y);
        g.quadraticCurveTo(
          top.x + Math.cos(a) * size * 0.8, top.y + Math.sin(a) * size * 0.3 - size * 0.2,
          top.x + Math.cos(a) * size * 1.1, top.y + Math.sin(a) * size * 0.5,
        );
        g.stroke({ color: 0x3d8a4a, width: 2, cap: 'round' });
      }
      break;
    }
    case 'birch': {
      g.ellipse(x + 2, y + size * 0.6, size * 0.6, size * 0.25);
      g.fill({ color: 0x000000, alpha: 0.15 });
      g.moveTo(x, y);
      g.lineTo(x, y + size * 0.85);
      g.stroke({ color: 0xe8e4d8, width: 2.5, cap: 'round' });
      // Bark stripes
      for (let i = 0; i < 3; i++) {
        const sy = y + size * (0.25 + i * 0.2);
        g.moveTo(x - 1, sy);
        g.lineTo(x + 1, sy);
        g.stroke({ color: 0x4a4438, width: 1 });
      }
      g.ellipse(x, y - size * 0.15, size * 0.75, size * 0.6);
      g.fill({ color: 0x8fc470, alpha: 0.95 });
      g.ellipse(x, y - size * 0.15, size * 0.5, size * 0.4);
      g.fill({ color: 0xa8d888 });
      break;
    }
    case 'willow': {
      g.ellipse(x + 2, y + size * 0.7, size * 0.75, size * 0.28);
      g.fill({ color: 0x000000, alpha: 0.15 });
      g.moveTo(x, y + size * 0.2);
      g.lineTo(x, y + size * 0.85);
      g.stroke({ color: 0x5a4a2a, width: 2.5, cap: 'round' });
      g.circle(x, y - size * 0.1, size * 0.55);
      g.fill({ color: 0x4a7a3a, alpha: 0.9 });
      // Drooping strands
      for (let i = 0; i < 6; i++) {
        const a = -Math.PI * 0.7 + (i / 5) * Math.PI * 0.4;
        const bx = x + Math.cos(a) * size * 0.5, by = y - size * 0.1 + Math.sin(a) * size * 0.3;
        g.moveTo(bx, by);
        g.lineTo(bx + (rng() - 0.5) * 3, by + size * 0.6);
        g.stroke({ color: 0x5a8a4a, width: 1.2, cap: 'round' });
      }
      break;
    }
    case 'baobab': {
      g.ellipse(x + 2, y + size * 0.7, size * 0.9, size * 0.3);
      g.fill({ color: 0x000000, alpha: 0.15 });
      // Fat bulbous trunk
      g.ellipse(x, y + size * 0.25, size * 0.55, size * 0.75);
      g.fill({ color: 0x9a7a4a });
      g.ellipse(x, y + size * 0.25, size * 0.55, size * 0.75);
      g.stroke({ color: 0x6a4a28, width: 1.5 });
      // Small scraggly tuft on top
      for (let i = 0; i < 4; i++) {
        const a = -Math.PI / 2 + (i - 1.5) * 0.5;
        g.moveTo(x, y - size * 0.5);
        g.lineTo(x + Math.cos(a) * size * 0.5, y - size * 0.5 + Math.sin(a) * size * 0.5 - size * 0.2);
        g.stroke({ color: 0x4a6a30, width: 3, cap: 'round' });
      }
      break;
    }
    case 'berry-bush': {
      g.ellipse(x + 1, y + size * 0.35, size * 0.6, size * 0.22);
      g.fill({ color: 0x000000, alpha: 0.12 });
      g.circle(x, y, size * 0.55);
      g.fill({ color: 0x3a7a3a });
      g.circle(x - size * 0.3, y + size * 0.1, size * 0.4);
      g.fill({ color: 0x3a7a3a });
      g.circle(x + size * 0.3, y + size * 0.1, size * 0.4);
      g.fill({ color: 0x3a7a3a });
      // Berries
      for (let i = 0; i < 5; i++) {
        const a = rng() * Math.PI * 2, d = rng() * size * 0.4;
        g.circle(x + Math.cos(a) * d, y + Math.sin(a) * d, 1.1);
        g.fill({ color: 0xd02828 });
      }
      break;
    }
    case 'flower-bush': {
      g.ellipse(x + 1, y + size * 0.35, size * 0.55, size * 0.2);
      g.fill({ color: 0x000000, alpha: 0.12 });
      g.circle(x, y, size * 0.5);
      g.fill({ color: 0x4a8a4a });
      // Flowers (yellow/white — never pink/purple)
      for (let i = 0; i < 4; i++) {
        const a = rng() * Math.PI * 2, d = rng() * size * 0.4;
        const fx = x + Math.cos(a) * d, fy = y + Math.sin(a) * d;
        g.circle(fx, fy, 1.4);
        g.fill({ color: i % 2 === 0 ? 0xf0d020 : 0xffffff });
      }
      break;
    }
    case 'fern-bush': {
      g.ellipse(x + 1, y + size * 0.3, size * 0.6, size * 0.2);
      g.fill({ color: 0x000000, alpha: 0.12 });
      for (let i = 0; i < 7; i++) {
        const a = -Math.PI + (i / 6) * Math.PI;
        const len = size * (0.5 + rng() * 0.3);
        g.moveTo(x, y + size * 0.2);
        g.quadraticCurveTo(
          x + Math.cos(a) * len * 0.5, y + size * 0.2 + Math.sin(a) * len * 0.5 - size * 0.2,
          x + Math.cos(a) * len, y + size * 0.2 + Math.sin(a) * len - size * 0.3,
        );
        g.stroke({ color: 0x2a6a3a, width: 1.5, cap: 'round' });
      }
      break;
    }
  }
}

/** Draw one island (shadow, beach, fill, border, trees) onto `g`. */
export function drawSingleIsland(
  g: Graphics,
  island: Island,
  rng: () => number,
): void {
  // Drop shadow
  drawIslandShape(g, island.points, 5, 7);
  g.fill({ color: 0x0d3d22, alpha: 0.35 });

  // Sandy beach ring (slightly larger than main fill)
  drawIslandShape(g, island.points, 0, 0);
  g.fill({ color: 0xc8a96e });

  // Main green fill (inset ~8px via scale trick — just draw slightly smaller visually)
  // We approximate by scaling points inward by a small factor toward the center
  const shrink = 0.88;
  const shrunken = island.points.map(p => ({
    x: island.cx + (p.x - island.cx) * shrink,
    y: island.cy + (p.y - island.cy) * shrink,
  }));
  g.moveTo(shrunken[0].x, shrunken[0].y);
  for (let i = 1; i < shrunken.length; i++) {
    g.lineTo(shrunken[i].x, shrunken[i].y);
  }
  g.closePath();
  g.fill({ color: 0x3d7a4a });

  // Border
  drawIslandShape(g, island.points, 0, 0);
  g.stroke({ color: 0x2a5a35, width: 3 });

  // Trees & bushes
  const treeCount = 3 + Math.floor(rng() * 6);
  for (let t = 0; t < treeCount; t++) {
    const angle = rng() * Math.PI * 2;
    const dist = rng() * island.radius * 0.5;
    const tx = island.cx + Math.cos(angle) * dist;
    const ty = island.cy + Math.sin(angle) * dist;
    const size = 6 + rng() * 5;
    const kind = PLANT_KINDS[Math.floor(rng() * PLANT_KINDS.length)];
    drawPlant(g, tx, ty, size, kind, rng);
  }
}

// Wrap a delta to [-half, +half] range for toroidal distance
function wrapDelta(d: number, size: number): number {
  if (d > size / 2) return d - size;
  if (d < -size / 2) return d + size;
  return d;
}

// ---- Polygon helpers ----

function pointInPolygon(px: number, py: number, pts: { x: number; y: number }[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const yi = pts[i].y, yj = pts[j].y;
    if ((yi > py) !== (yj > py) &&
        px < (pts[j].x - pts[i].x) * (py - yi) / (yj - yi) + pts[i].x) {
      inside = !inside;
    }
  }
  return inside;
}

function nearestPointOnSegment(
  px: number, py: number,
  ax: number, ay: number, bx: number, by: number,
): { x: number; y: number; dist: number } {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 < 0.001 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  const nx = ax + t * dx, ny = ay + t * dy;
  const d = Math.sqrt((px - nx) ** 2 + (py - ny) ** 2);
  return { x: nx, y: ny, dist: d };
}

function shrinkPolygon(island: Island, factor: number): { x: number; y: number }[] {
  return island.points.map(p => ({
    x: island.cx + (p.x - island.cx) * factor,
    y: island.cy + (p.y - island.cy) * factor,
  }));
}

/** Resolve position relative to island center, handling world wrapping. */
function resolveWrapped(pos: { x: number; y: number }, island: Island): { x: number; y: number } {
  return {
    x: island.cx + wrapDelta(pos.x - island.cx, WORLD_W),
    y: island.cy + wrapDelta(pos.y - island.cy, WORLD_H),
  };
}

// Find the nearest island within landing distance, or null.
export function findNearestIsland(
  pos: { x: number; y: number },
  islands: Island[],
  maxDist: number,
): Island | null {
  let best: Island | null = null;
  let bestDist = maxDist;
  for (const island of islands) {
    const dx = wrapDelta(pos.x - island.cx, WORLD_W);
    const dy = wrapDelta(pos.y - island.cy, WORLD_H);
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < bestDist) {
      bestDist = dist;
      best = island;
    }
  }
  return best;
}

// Check if a position is inside an island's walkable area (shrunken polygon).
export function isInsideIsland(
  pos: { x: number; y: number },
  island: Island,
): boolean {
  const e = resolveWrapped(pos, island);
  const inner = shrinkPolygon(island, 0.85);
  return pointInPolygon(e.x, e.y, inner);
}

// Find a valid landing position on the island nearest to the boat.
export function findLandingPosition(
  boatPos: { x: number; y: number },
  island: Island,
): { x: number; y: number } {
  const dx = wrapDelta(boatPos.x - island.cx, WORLD_W);
  const dy = wrapDelta(boatPos.y - island.cy, WORLD_H);
  const inner = shrinkPolygon(island, 0.85);

  // Walk from boat toward island center, find first point inside
  for (let t = 0; t <= 1; t += 0.02) {
    const tx = island.cx + dx * (1 - t);
    const ty = island.cy + dy * (1 - t);
    if (pointInPolygon(tx, ty, inner)) {
      return { x: tx, y: ty };
    }
  }
  return { x: island.cx, y: island.cy };
}

// Returns a push vector to resolve collision, or null if no collision.
// Uses polygon test against actual island shape.
export function checkCollision(
  pos: { x: number; y: number },
  islands: Island[],
): { x: number; y: number } | null {
  for (const island of islands) {
    const dx = wrapDelta(pos.x - island.cx, WORLD_W);
    const dy = wrapDelta(pos.y - island.cy, WORLD_H);
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Broad phase: skip far islands
    if (dist > island.radius * 1.5) continue;

    const ex = island.cx + dx;
    const ey = island.cy + dy;

    if (!pointInPolygon(ex, ey, island.points)) continue;

    // Find nearest edge point on polygon and push boat there + buffer
    let minDist = Infinity;
    let nearX = ex, nearY = ey;
    const pts = island.points;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      const np = nearestPointOnSegment(ex, ey, pts[j].x, pts[j].y, pts[i].x, pts[i].y);
      if (np.dist < minDist) {
        minDist = np.dist;
        nearX = np.x;
        nearY = np.y;
      }
    }

    if (minDist < 0.001) return { x: 5, y: 0 };

    const pushDx = nearX - ex;
    const pushDy = nearY - ey;
    const pushLen = Math.sqrt(pushDx * pushDx + pushDy * pushDy);
    const buffer = 3;
    return {
      x: pushDx + (pushDx / pushLen) * buffer,
      y: pushDy + (pushDy / pushLen) * buffer,
    };
  }
  return null;
}

// ---- Treasure chests ----
export interface Treasure {
  x: number;
  y: number;
}

export function generateTreasures(islands: Island[]): Treasure[] {
  const rng = makeRng(0x601dcafe);
  const treasures: Treasure[] = [];
  while (treasures.length < 60) {
    const x = rng() * WORLD_W;
    const y = rng() * WORLD_H;
    let onIsland = false;
    for (const isl of islands) {
      const dx = x - isl.cx, dy = y - isl.cy;
      if (dx * dx + dy * dy < isl.radius * isl.radius) { onIsland = true; break; }
    }
    if (onIsland) continue;
    treasures.push({ x, y });
  }
  return treasures;
}

export function drawTreasureChest(g: Graphics, x: number, y: number): void {
  // Chest body
  g.roundRect(x - 12, y - 6, 24, 16, 2);
  g.fill({ color: 0x8b6520, alpha: 0.6 });
  g.stroke({ color: 0x6b4510, width: 1.5, alpha: 0.5 });
  // Lid
  g.roundRect(x - 13, y - 11, 26, 7, 2);
  g.fill({ color: 0x9b7530, alpha: 0.6 });
  // Lock
  g.circle(x, y - 5, 2.5);
  g.fill({ color: 0xdaa520, alpha: 0.7 });
  // Gold
  g.circle(x - 5, y + 2, 2);
  g.circle(x + 5, y + 2, 2);
  g.circle(x, y + 5, 2);
  g.circle(x - 3, y + 6, 1.5);
  g.circle(x + 3, y + 6, 1.5);
  g.fill({ color: 0xffd700, alpha: 0.5 });
}
